import { BadRequestException, ConflictException, Injectable, NotFoundException, Optional } from '@nestjs/common';
import { CreateReviewRequest, ReviewEligibilityResponse, ReviewListResponse, ReviewsMineBatchResponse, ReviewSummary, ReviewTypeCode, UpdateReviewRequest } from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { OrdersRepository } from '../orders/orders.repository';
import { ProductsRepository } from '../products/products.repository';
import { DEFAULT_ORGANIZATION_ID, StoreContextService } from '../tenancy/store-context.service';
import { ReviewsRepository } from './reviews.repository';

const REVIEW_TYPES: ReviewTypeCode[] = ['product', 'delivery_partner', 'order', 'general'];

@Injectable()
export class ReviewsService {
  constructor(
    private readonly reviewsRepository: ReviewsRepository,
    private readonly productsRepository: ProductsRepository,
    private readonly ordersRepository: OrdersRepository,
    @Optional()
    private readonly auditService?: AuditService,
    @Optional()
    private readonly storeContext?: StoreContextService
  ) {}

  async list(reviewType: ReviewTypeCode, targetId?: string): Promise<ReviewListResponse> {
    const reviewTypeId = await this.resolveReviewTypeId(reviewType);

    return this.reviewsRepository.list(reviewTypeId, targetId);
  }

  async mine(userId: string, reviewType: ReviewTypeCode, targetId?: string): Promise<ReviewSummary | undefined> {
    const reviewTypeId = await this.resolveReviewTypeId(reviewType);

    return this.reviewsRepository.findMine(reviewTypeId, userId, targetId);
  }

  /**
   * Same as `mine()` but for many targets of one review type at once — lets a
   * list screen (e.g. all delivered orders) fetch "my rating" for every row
   * in one request instead of one request per row.
   */
  async mineBatch(userId: string, reviewType: ReviewTypeCode, targetIds: string[]): Promise<ReviewsMineBatchResponse> {
    const reviewTypeId = await this.resolveReviewTypeId(reviewType);
    const reviews = await this.reviewsRepository.findMineBatch(reviewTypeId, userId, targetIds);

    const result: ReviewsMineBatchResponse = {};
    for (const review of reviews) {
      if (review.targetId) result[review.targetId] = review;
    }
    return result;
  }

  /**
   * Lets the UI decide whether to show a "write a review" affordance before
   * the shopper tries — reuses the exact same rule `create()` enforces, so
   * the hint and the enforcement can never drift apart.
   */
  async eligibility(userId: string, reviewType: ReviewTypeCode, targetId?: string): Promise<ReviewEligibilityResponse> {
    if (!REVIEW_TYPES.includes(reviewType)) {
      return { eligible: false };
    }

    try {
      await this.validateTarget(reviewType, targetId, userId);
      return { eligible: true };
    } catch {
      return { eligible: false };
    }
  }

  async create(userId: string, input: CreateReviewRequest): Promise<ReviewSummary> {
    this.validateRating(input.rating);
    const reviewTypeId = await this.resolveReviewTypeId(input.reviewType);
    await this.validateTarget(input.reviewType, input.targetId, userId);

    if (await this.reviewsRepository.exists(reviewTypeId, userId, input.targetId)) {
      throw new ConflictException('You have already submitted this review');
    }

    const review = await this.reviewsRepository.create({
      reviewTypeId,
      merchantId: this.merchantId(),
      userId,
      targetId: input.targetId,
      rating: input.rating,
      title: input.title,
      comment: input.comment,
      attributes: input.attributes,
    });

    await this.auditService?.logChange({
      schemaName: 'reviews',
      tableName: 'reviews',
      recordId: review.id,
      operation: 'create',
      changedColumns: ['reviewType', 'targetId', 'rating', 'comment'],
      newValue: review,
      changedBy: userId,
      source: 'ReviewsService.create',
    });

    return review;
  }

  async update(userId: string, reviewId: string, input: UpdateReviewRequest): Promise<ReviewSummary> {
    if (input.rating !== undefined) this.validateRating(input.rating);

    const updated = await this.reviewsRepository.update(reviewId, userId, input);

    if (!updated) {
      throw new NotFoundException('Review not found');
    }

    await this.auditService?.logChange({
      schemaName: 'reviews',
      tableName: 'reviews',
      recordId: updated.id,
      operation: 'update',
      changedColumns: Object.keys(input),
      newValue: updated,
      changedBy: userId,
      source: 'ReviewsService.update',
    });

    return updated;
  }

  async delete(userId: string, reviewId: string): Promise<void> {
    const deleted = await this.reviewsRepository.delete(reviewId, userId);

    if (!deleted) {
      throw new NotFoundException('Review not found');
    }

    await this.auditService?.logChange({
      schemaName: 'reviews',
      tableName: 'reviews',
      recordId: reviewId,
      operation: 'delete',
      changedBy: userId,
      source: 'ReviewsService.delete',
    });
  }

  private async resolveReviewTypeId(code: ReviewTypeCode): Promise<string> {
    if (!REVIEW_TYPES.includes(code)) {
      throw new BadRequestException(`Unknown review type: ${code}`);
    }

    const reviewTypeId = await this.reviewsRepository.findReviewTypeId(code);

    if (!reviewTypeId) {
      throw new BadRequestException(`Review type "${code}" is not configured`);
    }

    return reviewTypeId;
  }

  private async validateTarget(reviewType: ReviewTypeCode, targetId: string | undefined, userId: string): Promise<void> {
    if (reviewType === 'general') return;

    if (!targetId) {
      throw new BadRequestException(`targetId is required for a "${reviewType}" review`);
    }

    if (reviewType === 'product') {
      const product = await this.productsRepository.findById(targetId);
      if (!product) throw new NotFoundException('Product not found');

      if (!(await this.hasDeliveredOrderWithProduct(userId, targetId))) {
        throw new BadRequestException('You can only review products from a delivered order');
      }
      return;
    }

    // 'order' and 'delivery_partner' both review a specific order — there is
    // no standalone delivery-partner directory, so the delivery executive is
    // reviewed per-order, same as the shipping-experience review.
    const order = await this.ordersRepository.findById(targetId, userId);
    if (!order) throw new NotFoundException('Order not found');

    if (order.status !== 'delivered') {
      throw new BadRequestException('You can only rate an order once it has been delivered');
    }
  }

  private async hasDeliveredOrderWithProduct(userId: string, productId: string): Promise<boolean> {
    const orders = await this.ordersRepository.list(userId);
    return orders.some(o => o.status === 'delivered' && o.items.some(item => item.productId === productId));
  }

  private validateRating(rating: number): void {
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      throw new BadRequestException('rating must be an integer between 1 and 5');
    }
  }

  private merchantId(): string {
    return this.storeContext?.organizationId() ?? DEFAULT_ORGANIZATION_ID;
  }
}
